<template>
  <!-- Picks the files to send with a ticket or a message. The full form shows
       a drop zone (a new ticket); `compact` shows only the picked files, for
       a composer that has its own paperclip and takes drops itself. Either
       way the picked files are the v-model, and the parent sends them. -->
  <div class="ta-picker" :class="{ 'ta-picker--compact': compact, 'is-disabled': disabled }">
    <div
      v-if="!compact"
      class="ta-drop"
      :class="{ 'is-dragover': isDragOver }"
      role="button"
      tabindex="0"
      :aria-disabled="disabled ? 'true' : 'false'"
      aria-label="Add files"
      @click="open"
      @keydown.enter.prevent="open"
      @keydown.space.prevent="open"
      @dragenter.prevent="onDragOver"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="onDrop"
    >
      <span class="ta-drop__icon" aria-hidden="true"><i class="pi pi-cloud-upload"></i></span>
      <span class="ta-drop__title">Drop files here, or <span class="ta-drop__browse">browse</span></span>
      <span class="ta-drop__hint">
        Screenshots, PDFs, documents or short videos — up to {{ maxFiles }} files, {{ maxMb }}MB each
      </span>
    </div>

    <ul v-if="modelValue.length" class="ta-chips" aria-label="Files to send">
      <li v-for="(file, index) in modelValue" :key="fileKey(file)" class="ta-chip" :title="file.name">
        <span class="ta-chip__icon">
          <img v-if="previewOf(file)" :src="previewOf(file)" alt="" />
          <i v-else class="pi" :class="attachmentIcon(file)" aria-hidden="true"></i>
        </span>
        <span class="ta-chip__name">{{ file.name }}</span>
        <span class="ta-chip__size">{{ formatFileSize(file.size) }}</span>
        <button
          type="button"
          class="ta-chip__remove"
          :disabled="disabled"
          :aria-label="`Remove ${file.name}`"
          @click="remove(index)"
        >
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </li>
    </ul>

    <input
      ref="input"
      type="file"
      class="ta-input"
      multiple
      :accept="accept"
      tabindex="-1"
      aria-hidden="true"
      @change="onChange"
    />
  </div>
</template>
<script>
import { TICKET_ATTACHMENT_MAX_FILES, TOAST_GLOBAL_CONFIG } from '@/utils/const';
import {
  acceptAttachments,
  attachmentIcon,
  formatFileSize,
  isImageFile,
  TICKET_ATTACHMENT_ACCEPT,
  TICKET_ATTACHMENT_MAX_MB,
} from '@/utils/ticket';

export default {
  name: 'TicketAttachmentPicker',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    compact: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isDragOver: false,
      accept: TICKET_ATTACHMENT_ACCEPT,
      maxFiles: TICKET_ATTACHMENT_MAX_FILES,
      maxMb: TICKET_ATTACHMENT_MAX_MB,
      // Thumbnails of the picked images, keyed by the file's own key below.
      previews: {},
    };
  },
  watch: {
    modelValue(files) {
      this.syncPreviews(files);
    },
  },
  methods: {
    attachmentIcon,
    formatFileSize,
    fileKey(file) {
      return `${file.name}:${file.size}:${file.lastModified}`;
    },
    /** Opens the file dialog — also for a parent with its own button. */
    open() {
      if (this.disabled) return;
      this.$refs.input?.click();
    },
    onChange(event) {
      this.addFiles(event.target.files);
      // The same file picked again still fires a change.
      event.target.value = '';
    },
    onDragOver() {
      if (!this.disabled) this.isDragOver = true;
    },
    onDrop(event) {
      this.isDragOver = false;
      if (this.disabled) return;
      this.addFiles(event.dataTransfer?.files);
    },
    /** Adds what was picked, dropped or pasted; says why anything was left out. */
    addFiles(list) {
      if (this.disabled || !list || !list.length) return;
      const { files, errors } = acceptAttachments(this.modelValue, list);
      errors.forEach((message) => this.$toast.error(message, TOAST_GLOBAL_CONFIG));
      if (files.length !== this.modelValue.length) this.$emit('update:modelValue', files);
    },
    remove(index) {
      if (this.disabled) return;
      this.$emit('update:modelValue', this.modelValue.filter((_, i) => i !== index));
    },
    previewOf(file) {
      return this.previews[this.fileKey(file)] || '';
    },
    // Object URLs are made once per image and released as soon as it is gone.
    syncPreviews(files) {
      const keep = new Set(files.map((file) => this.fileKey(file)));
      const previews = { ...this.previews };
      Object.keys(previews).forEach((key) => {
        if (keep.has(key)) return;
        URL.revokeObjectURL(previews[key]);
        delete previews[key];
      });
      files.forEach((file) => {
        const key = this.fileKey(file);
        if (!previews[key] && isImageFile(file.name, file.type)) previews[key] = URL.createObjectURL(file);
      });
      this.previews = previews;
    },
  },
  mounted() {
    this.syncPreviews(this.modelValue);
  },
  beforeUnmount() {
    Object.values(this.previews).forEach((url) => URL.revokeObjectURL(url));
    this.previews = {};
  },
};
</script>
